<template>
  <div class="button-wrapper">
    <slot v-bind="defaultScope"></slot>
    <a
      :href="generatedUrl"
      :class="buttonClass"
      target="book"
      @click.prevent="handleClick"
      @keydown.space.prevent="handleClick"
    >
      {{ buttonLabel }}
    </a>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  export default {
    name: 'BookButton',
    props: {
      /**
       * When the page loads with this parameter in the URL, the main widget will open
       * automatically.
       */
      openBooking: {
        type: Boolean,
        default: false,
      },
      /**
       * Updates the parameters in localQuery during initialization. All booking
       * widget activators on the page will open the booking widget with the same parameters.
       */
      preselectData: {
        type: Boolean,
        default: false,
      },
      /**
       * Array of hotel data to be registered in the [hotelData store module](/docs/api/store/hoteldata)
       */
      hotelOptions: {
        type: Array,
        default: () => []
      },
      /**
       * Button classes
       */
      buttonClass: {
        type: String,
        default: 'book',
      },
      /**
       * Button label
       */
      buttonLabel: {
        type: String,
        default: 'Book',
      },
      /**
       * Hotel SynXis code
       */
      hotelCode: {
        type: String,
        default: '',
      },
      /**
       * Array of room SynXis codes
       */
      roomCodes: {
        type: [Array, String],
        default: () => [],
      },
      /**
       * SynXis rate code
       */
      rateCode: {
        type: String,
        default: '',
      },
      /**
       * Hotel name
       */
      roomName: {
        type: String,
        default: '',
      },
      /**
       * Bed type SynXis code
       */
      bedType: {
        type: String,
        default: '',
      },
      /**
       * Bed name
       */
      bedName: {
        type: String,
        default: '',
      },
      /**
       * Number of adult guests
       */
      adultGuestCount: {
        type: Number,
        default: null,
      },
      /**
       * Number of child guests
       */
      childGuestCount: {
        type: Number,
        default: null,
      },
      /**
       * Number of child guests for each additional room
       */
      aditionalGuests: {
        type: Array,
        default: () => [],
      },
      /**
       * Number of rooms in booking
       */
      roomQuantity: {
        type: Number,
        default: null,
      },
      /**
       * Start date in dayFormat
       */
      stayDateStart: {
        type: String,
        default: '',
      },
      /**
       * End date in dayFormat
       */
      stayDateEnd: {
        type: String,
        default: '',
      },
      /**
       * Travel agent number
       */
      iataNumber: {
        type: String,
        default: '',
      },
      /**
       * Code for group discount
       */
      groupCode: {
        type: String,
        default: '',
      },
      /**
       * Code for promotional discount
       */
      promotionCode: {
        type: String,
        default: '',
      },
    },
    data: () => ({
      selectedRoom: null,
    }),
    created() {
      this.initComponent()
    },
    computed: {
      ...mapGetters('i18n', ['tc']),
      defaultScope() {
        return {
          onChange: this.onChange,
        }
      },
      generatedUrl() {
        const baseParams = {
          hotelCode: this.hotelCode,
          roomCodes: this.getRoomCode(),
          rateCode: this.rateCode,
          roomName: this.roomName,
          bedType: this.bedType,
          bedName: this.bedName,
          adultGuestCount: this.adultGuestCount,
          childGuestCount: this.childGuestCount,
          roomQuantity: this.roomQuantity,
          stayDateStart: this.stayDateStart,
          stayDateEnd: this.stayDateEnd,
          iataNumber: this.iataNumber,
          groupCode: this.groupCode,
          promotionCode: this.promotionCode,
          openBooking: this.openBooking ? 'true' : undefined,
        }

        const params = new URLSearchParams()

        Object.entries(baseParams)
          .forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              params.append(key, value)
            }
          })

        if (Array.isArray(this.aditionalGuests)) {
          this.aditionalGuests.forEach((guest, index) => {
            if (guest.adultGuestCount != null) {
              params.append(`additionalGuests[${index}][adultGuestCount]`, guest.adultGuestCount)
            }
            if (guest.childGuestCount != null) {
              params.append(`additionalGuests[${index}][childGuestCount]`, guest.childGuestCount)
            }
          })
        }

        return `?${params.toString()}`
      }
    },
    methods: {
      ...mapActions('booking', ['updateQuery']),
      ...mapActions('hotelData', ['registerHotels', 'registerSpecificHotels']),
      initComponent() {
        if (this.preselectData) {
          const exclude = [
            'preselectData',
            'buttonLabel',
            'buttonClass',
            'openBooking',
          ]

          Object.entries(this.$props).forEach(([key, value]) => {
            if (!exclude.includes(key) && value !== undefined && value !== null && value !== '') {
              this.updateQuery({ prop: key, value })
            }
          })
          if (this.hotelOptions?.length > 0) {
            this.registerSpecificHotels(this.hotelOptions)
          }
        }
      },
      handleClick() {
        if (this.hotelOptions?.length > 0) {
          this.registerHotels({ hotels: this.hotelOptions })
        }
      },
      onChange(e) {
        const { value } = e.target
        this.selectedRoom = value
        this.updateQuery({ prop: 'roomCodes', value })
      },
      getRoomCode() {
        if (this.selectedRoom && String(this.selectedRoom).trim() !== '') {
          return JSON.stringify([this.selectedRoom])
        }
        if (this.roomCodes && this.roomCodes.length > 0) {
          return JSON.stringify(Array.isArray(this.roomCodes) ? this.roomCodes : [this.roomCodes])
        }
        return undefined
      },
    }
  }
</script>
